import { HTMLAttributes, useCallback, useState } from 'preact/compat'
import { useRecentlyAddedIds } from '../../../hooks'
import {
  DrawerBottomSheet,
  DrawerBottomHeader,
  DrawerBottomContent,
  DrawerBottomFooter,
  BarcodeList,
  ListItem,
  Button,
  ToastMessage,
} from '../../atoms'
import type { StorableItem } from '../../../store/ResultItemsListStore'
import { ReadonlySignal, Signal } from '@preact/signals'
import { Text } from 'preact-i18n'

function RedText(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <div style="color: var(--scandit-color-secondary-red-100); font-weight: 400;cursor: pointer;" {...props}>
      {props.children}
    </div>
  )
}

export function ExpandableItemsListDrawer({
  totalQuantity,
  items,
  visible,
  expanded,
  showDrawerBottomSheetArrow = true,
  onClearList,
  onExpandChange,
  onResumeScanning,
}: {
  showDrawerBottomSheetArrow?: boolean
  totalQuantity: ReadonlySignal<number>
  items: Signal<StorableItem[]>
  visible: boolean
  expanded: boolean
  onClearList: () => void
  onExpandChange: (isExpanded: boolean) => void
  onResumeScanning: () => void
}) {
  const [pressedSet, setPressedSet] = useState<Set<string>>(new Set())
  const [shouldShowToastMessage, setShowToastMessage] = useState(false)
  const recentlyAdded = useRecentlyAddedIds(items, 3000)

  const onLongPress = useCallback(async ({ data, id }: StorableItem) => {
    setPressedSet((oldSet) => {
      const newSet = new Set(oldSet)
      newSet.add(id)
      return newSet
    })
    try {
      if (data != null) {
        await navigator.clipboard.writeText(data)
        setShowToastMessage(true)
      }
    } catch (error: unknown) {
      console.warn('Cannot copy to clipboard', error)
    }
  }, [])

  const onReleasePress = useCallback(({ id }: StorableItem) => {
    setPressedSet((oldSet) => {
      const newSet = new Set(oldSet)
      newSet.delete(id)
      return newSet
    })
  }, [])

  const onHandleClick = useCallback(() => {
    onExpandChange(!expanded)
  }, [expanded, onExpandChange])

  return (
    <>
      <DrawerBottomSheet
        id="itemsList"
        visible={visible}
        expanded={expanded}
        onHandleClick={onHandleClick}
        showArrow={showDrawerBottomSheetArrow}
      >
        <DrawerBottomHeader>
          <b>
            <Text
              id="expandableList.itemsScanned"
              plural={totalQuantity.value}
              fields={{ count: totalQuantity.value }}
            />
          </b>
          <RedText onClick={onClearList}>
            <Text id="expandableList.clearList" />
          </RedText>
        </DrawerBottomHeader>
        <DrawerBottomContent>
          <BarcodeList>
            {items.value.map((item) => (
              <ListItem
                item={item}
                highlighted={pressedSet.has(item.id) || recentlyAdded.has(item.id)}
                onLongPress={onLongPress}
                onReleasePress={onReleasePress}
              />
            ))}
          </BarcodeList>
        </DrawerBottomContent>
        <DrawerBottomFooter>
          <Button onClick={onResumeScanning}>
            <Text id="expandableList.resumeScanning" />
          </Button>
        </DrawerBottomFooter>
      </DrawerBottomSheet>
      <ToastMessage timeout={3000} show={shouldShowToastMessage} onFinishedTimeout={() => setShowToastMessage(false)}>
        <Text id="expandableList.copiedToClipboard" />
      </ToastMessage>
    </>
  )
}
